import { useState } from 'react';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FolderKanban, Plus, Star, ExternalLink, Github } from 'lucide-react';
import SEO from '@/components/SEO';

const projectData = {
  backlog: [
    {
      title: 'Inventory Management Dashboard',
      difficulty: 3,
      skills: ['React', 'TypeScript', 'REST APIs'],
      duration: '2-3 weeks',
      description: 'Build a supply chain tracking system'
    },
    {
      title: 'Chat Application',
      difficulty: 4,
      skills: ['React', 'WebSockets', 'Node.js'],
      duration: '3-4 weeks',
      description: 'Real-time messaging platform'
    }
  ],
  inProgress: [
    {
      title: 'Task Manager App',
      difficulty: 2,
      skills: ['React', 'JavaScript', 'CSS'],
      duration: '1-2 weeks',
      description: 'Simple to-do list with priorities',
      progress: 65,
      github: 'https://github.com/user/task-manager',
      demo: 'https://task-manager-demo.vercel.app'
    }
  ],
  completed: [
    {
      title: 'Portfolio Website',
      difficulty: 1,
      skills: ['HTML', 'CSS', 'JavaScript'],
      duration: '1 week',
      description: 'Personal portfolio site',
      progress: 100,
      github: 'https://github.com/user/portfolio',
      demo: 'https://myportfolio.com'
    }
  ]
};

interface ProjectCardProps {
  project: {
    title: string;
    difficulty: number;
    skills: string[];
    duration: string;
    description: string;
    progress?: number;
    github?: string;
    demo?: string;
  };
  status: 'backlog' | 'inProgress' | 'completed';
}

function ProjectCard({ project, status }: ProjectCardProps) {
  return (
    <Card className="transition-colors duration-200 hover:shadow-md">
      <CardHeader>
        <div className="flex items-start justify-between">
          <CardTitle className="text-base">{project.title}</CardTitle>
          <div className="flex gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`h-3 w-3 ${
                  i < project.difficulty ? 'fill-primary text-primary' : 'text-muted'
                }`}
              />
            ))}
          </div>
        </div>
        <CardDescription className="text-xs">{project.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {project.skills.map((skill, idx) => (
            <Badge key={idx} variant="branch" className="text-xs">
              {skill}
            </Badge>
          ))}
        </div>
        <div className="text-xs text-muted-foreground">
          Estimated: {project.duration}
        </div>
        {status === 'inProgress' && project.progress !== undefined && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{project.progress}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${project.progress}%` }}
              />
            </div>
          </div>
        )}
        <div className="flex gap-2 pt-2">
          {status === 'backlog' && (
            <Button size="sm" className="w-full">
              Start Project
            </Button>
          )}
          {status === 'inProgress' && (
            <>
              <Button size="sm" variant="outline" className="flex-1">
                View Details
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <a href={project.github} target="_blank" rel="noopener noreferrer">
                  <Github className="h-4 w-4" />
                </a>
              </Button>
            </>
          )}
          {status === 'completed' && (
            <>
              <Button size="sm" variant="outline" asChild className="flex-1">
                <a href={project.demo} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View Demo
                </a>
              </Button>
              <Button size="sm" variant="ghost" asChild>
                <a href={project.github} target="_blank" rel="noopener noreferrer">
                  <Github className="h-4 w-4" />
                </a>
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function Projects() {
  const { isMobile } = useSidebar();
  const [activeTab, setActiveTab] = useState('in-progress');

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground space-y-6 pb-16">
      <SEO title="Projects - VeteranOS" description="Portfolio builder and project management" />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-border pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-primary flex items-center gap-2">
            <FolderKanban className="h-7 w-7" />
            Projects
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Build your portfolio with MOS-specific projects
          </p>
        </div>
        <div className="flex items-center gap-4 mt-4 md:mt-0">
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
          {isMobile && <SidebarTrigger />}
        </div>
      </div>

      {/* Tabs for Kanban Columns */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="backlog">
            Backlog ({projectData.backlog.length})
          </TabsTrigger>
          <TabsTrigger value="in-progress">
            In Progress ({projectData.inProgress.length})
          </TabsTrigger>
          <TabsTrigger value="completed">
            Completed ({projectData.completed.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="backlog" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projectData.backlog.map((project, idx) => (
              <ProjectCard key={idx} project={project} status="backlog" />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="in-progress" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projectData.inProgress.map((project, idx) => (
              <ProjectCard key={idx} project={project} status="inProgress" />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projectData.completed.map((project, idx) => (
              <ProjectCard key={idx} project={project} status="completed" />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
